<div class="min-h-screen bg-gray-900 text-gray-100 p-6">
  <div class="max-w-7xl mx-auto">
    <app-notification></app-notification>

    <!-- Header -->
    <div class="mb-6">
      <h1 class="text-3xl font-bold text-gray-100 mb-2">Media Bibliotheek</h1>
      <p class="text-gray-400">Beheer en upload media bestanden voor gebruik op de websites</p>
    </div>

    <!-- Stats Section -->
    <div *ngIf="stats && stats.totalFiles > 0" class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      <div class="bg-gray-800/50 rounded-lg border border-gray-700/50 p-4">
        <p class="text-sm text-gray-400">Totaal bestanden</p>
        <p class="text-2xl font-bold text-gray-100">{{ stats.totalFiles }}</p>
      </div>
      <div class="bg-gray-800/50 rounded-lg border border-gray-700/50 p-4">
        <p class="text-sm text-gray-400">Totale grootte</p>
        <p class="text-2xl font-bold text-gray-100">{{ formatFileSize(stats.totalSizeBytes) }}</p>
      </div>
      <div class="bg-gray-800/50 rounded-lg border border-gray-700/50 p-4">
        <p class="text-sm text-gray-400">Afbeeldingen</p>
        <p class="text-2xl font-bold text-indigo-400">{{ stats.imageCount }}</p>
      </div>
      <div class="bg-gray-800/50 rounded-lg border border-gray-700/50 p-4">
        <p class="text-sm text-gray-400">Video &amp; overig</p>
        <p class="text-2xl font-bold text-gray-100">{{ stats.videoCount + stats.otherCount }}</p>
      </div>
    </div>

    <!-- Migration Banner (shown when no stats yet) -->
    <div *ngIf="stats && stats.totalFiles === 0 && !migrating" class="bg-amber-900/30 border border-amber-700/50 rounded-lg p-4 mb-6 flex items-center justify-between">
      <div>
        <p class="text-sm text-amber-200 font-medium">Bestaande bestanden indexeren</p>
        <p class="text-xs text-amber-300/70">Eenmalige actie om bestaande bestanden te indexeren voor snellere weergave en statistieken.</p>
      </div>
      <button
        (click)="migrateExistingFiles()"
        class="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 transition-colors whitespace-nowrap">
        Indexeren
      </button>
    </div>

    <!-- Migration Progress -->
    <div *ngIf="migrating" class="bg-gray-800/50 border border-gray-700/50 rounded-lg p-4 mb-6">
      <div class="flex items-center gap-3">
        <svg class="animate-spin w-5 h-5 text-indigo-400" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span class="text-sm text-gray-300">{{ migrationProgress }}</span>
      </div>
    </div>

    <!-- Upload Section -->
    <div class="bg-gray-800/50 rounded-lg border border-gray-700/50 backdrop-blur-sm p-6 mb-6">
      <h2 class="text-xl font-semibold text-gray-100 mb-4">Bestand Uploaden</h2>
      
      <div class="flex items-center gap-4">
        <label for="fileInput" class="cursor-pointer inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors">
          <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
          Bestand Selecteren
        </label>
        <input
          id="fileInput"
          type="file"
          (change)="onFileSelected($event)"
          class="hidden"
          accept="image/*,video/*,audio/*,application/pdf"
        />
        
        <div *ngIf="selectedFile" class="flex-1">
          <span class="text-gray-300">{{ selectedFile.name }}</span>
          <span class="text-gray-500 ml-2">({{ formatFileSize(selectedFile.size) }})</span>
          <div *ngIf="selectedFile.type.startsWith('image/') && !uploading" class="mt-2 text-xs text-gray-400">
            <p>Deze afbeelding wordt geüpload als:</p>
            <ul class="list-disc list-inside mt-1 space-y-0.5">
              <li>Origineel bestand</li>
              <li>WebP variant (max 1400px breed)</li>
              <li>WebP variant (max 600px breed)</li>
              <li>WebP variant (max 200px breed)</li>
            </ul>
          </div>
          
          <!-- Upload Progress -->
          <div *ngIf="uploading" class="mt-4">
            <div class="flex items-center justify-between mb-1">
              <span class="text-xs text-gray-400">{{ uploadStatus }}</span>
              <span class="text-xs text-gray-400">{{ uploadProgress }}%</span>
            </div>
            <div class="w-full bg-gray-700 rounded-full h-2.5">
              <div 
                class="bg-indigo-600 h-2.5 rounded-full transition-all duration-300 ease-out"
                [style.width]="uploadProgress + '%'"
              ></div>
            </div>
          </div>
        </div>
        
        <button
          (click)="uploadFile()"
          [disabled]="!selectedFile || uploading"
          class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <svg *ngIf="!uploading" class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
          </svg>
          <svg *ngIf="uploading" class="animate-spin w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          {{ uploading ? 'Uploaden...' : 'Uploaden' }}
        </button>
      </div>
    </div>

    <!-- Media Files Grid -->
    <div class="bg-gray-800/50 rounded-lg border border-gray-700/50 backdrop-blur-sm p-6">
      <div class="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 class="text-xl font-semibold text-gray-100">Geüploade Bestanden</h2>
        <div class="flex items-center gap-2 flex-wrap">
          <!-- View Mode Toggle -->
          <div class="inline-flex rounded-md shadow-sm border border-gray-600" role="group">
            <button
              (click)="viewMode = 'grid'; saveViewMode('grid')"
              [class]="viewMode === 'grid' 
                ? 'bg-indigo-600 text-white' 
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'"
              class="px-3 py-1.5 text-sm font-medium rounded-l-md transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500"
              title="Grid weergave"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </button>
            <button
              (click)="viewMode = 'list'; saveViewMode('list')"
              [class]="viewMode === 'list' 
                ? 'bg-indigo-600 text-white' 
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'"
              class="px-3 py-1.5 text-sm font-medium rounded-r-md transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500"
              title="Lijst weergave"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
          <button
            (click)="loadMediaFiles()"
            [disabled]="loading"
            class="inline-flex items-center px-3 py-1.5 border border-gray-600 text-sm font-medium rounded-md text-gray-300 bg-gray-700 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 transition-colors"
          >
            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Vernieuwen
          </button>
          <!-- Pagination -->
          <div *ngIf="hasPrevPage || hasNextPage" class="inline-flex items-center gap-2 border border-gray-600 rounded-md overflow-hidden">
            <button
              type="button"
              (click)="prevPage()"
              [disabled]="!hasPrevPage || loading"
              class="px-3 py-1.5 text-sm font-medium text-gray-300 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <span class="px-3 py-1.5 text-sm text-gray-400">Pagina {{ currentPageIndex + 1 }}</span>
            <button
              type="button"
              (click)="nextPage()"
              [disabled]="!hasNextPage || loading"
              class="px-3 py-1.5 text-sm font-medium text-gray-300 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <div *ngIf="loading" class="text-center py-12">
        <svg class="animate-spin h-8 w-8 text-indigo-500 mx-auto" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <p class="text-gray-400 mt-4">Bestanden laden...</p>
      </div>

      <div *ngIf="!loading && mediaFiles.length === 0" class="text-center py-12">
        <svg class="w-16 h-16 text-gray-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        <p class="text-gray-400">Nog geen bestanden geüpload</p>
      </div>

      <!-- Grid View -->
      <div *ngIf="!loading && mediaFiles.length > 0 && viewMode === 'grid'" class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <div *ngFor="let file of mediaFiles" class="bg-gray-900/50 rounded-lg border border-gray-700/50 overflow-hidden hover:border-gray-600 transition-colors">
          <!-- Preview -->
          <div class="aspect-square bg-gray-800 flex items-center justify-center relative group">
            <img *ngIf="isImage(file.contentType)" [src]="file.url" [alt]="file.name" class="w-full h-full object-cover" />
            <div *ngIf="isVideo(file.contentType)" class="w-full h-full flex items-center justify-center">
              <svg class="w-16 h-16 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </div>
            <div *ngIf="isAudio(file.contentType)" class="w-full h-full flex items-center justify-center">
              <svg class="w-16 h-16 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
              </svg>
            </div>
            <div *ngIf="!isImage(file.contentType) && !isVideo(file.contentType) && !isAudio(file.contentType)" class="w-full h-full flex items-center justify-center">
              <svg class="w-16 h-16 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            
            <!-- Overlay Actions -->
            <div class="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
              <button *ngIf="isImage(file.contentType)" (click)="openImageModal(file)" class="p-2 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors" title="Bekijken">
                <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </button>
              <a *ngIf="!isImage(file.contentType)" [href]="file.url" target="_blank" class="p-2 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors" title="Openen">
                <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
              <button (click)="copyUrl(file.url)" class="p-2 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors" title="URL Kopiëren">
                <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </button>
              <button (click)="openDeleteModal(file)" class="p-2 bg-red-600 rounded-lg hover:bg-red-700 transition-colors" title="Verwijderen">
                <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          </div>
          
          <!-- File Info -->
          <div class="p-3">
            <p class="text-sm font-medium text-gray-200 truncate mb-1" [title]="file.name">{{ file.name }}</p>
            <p class="text-xs text-gray-500">{{ formatFileSize(file.size) }}</p>
            <p class="text-xs text-gray-500 mt-1">{{ file.contentType }}</p>
          </div>
        </div>
      </div>

      <!-- List View (Images Only) -->
      <div *ngIf="!loading && mediaFiles.length > 0 && viewMode === 'list'" class="space-y-2">
        <div *ngFor="let file of getImageFiles()" class="bg-gray-900/50 rounded-lg border border-gray-700/50 hover:border-gray-600 transition-colors">
          <div class="flex items-center gap-4 p-4">
            <!-- Thumbnail -->
            <div class="flex-shrink-0 w-24 h-24 bg-gray-800 rounded-lg overflow-hidden">
              <img [src]="file.url" [alt]="file.name" class="w-full h-full object-cover" />
            </div>
            
            <!-- File Info -->
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-gray-200 truncate mb-1" [title]="file.name">{{ file.name }}</p>
              <div class="flex items-center gap-4 text-xs text-gray-500">
                <span>{{ formatFileSize(file.size) }}</span>
                <span>{{ file.contentType }}</span>
                <span>{{ formatDate(file.timeCreated) }}</span>
              </div>
              <div class="mt-2 space-y-1">
                <div class="flex items-center gap-2">
                  <label class="text-xs text-gray-400 w-20">Variant:</label>
                  <select 
                    (change)="onVariantChange($event, file)"
                    class="flex-1 px-2 py-1 text-xs bg-gray-800 border border-gray-700 rounded text-gray-300 font-mono"
                  >
                    <option [value]="file.url">Origineel</option>
                    <option *ngIf="file.variants && file.variants.webp1400" [value]="file.variants.webp1400">WebP 1400px</option>
                    <option *ngIf="file.variants && file.variants.webp600" [value]="file.variants.webp600">WebP 600px</option>
                    <option *ngIf="file.variants && file.variants.webp200" [value]="file.variants.webp200">WebP 200px</option>
                  </select>
                </div>
                <input 
                  type="text" 
                  [id]="'url-input-' + file.fullPath"
                  [value]="file.url" 
                  readonly
                  class="w-full px-2 py-1 text-xs bg-gray-800 border border-gray-700 rounded text-gray-300 font-mono"
                />
              </div>
            </div>
            
            <!-- Actions -->
            <div class="flex-shrink-0 flex items-center gap-2">
              <button (click)="openImageModal(file)" class="p-2 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors" title="Bekijken">
                <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </button>
              <button (click)="copyUrl(file.url)" class="p-2 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors" title="URL Kopiëren">
                <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </button>
              <button (click)="openDeleteModal(file)" class="p-2 bg-red-600 rounded-lg hover:bg-red-700 transition-colors" title="Verwijderen">
                <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          </div>
        </div>
        
        <!-- Empty state for list view when no images -->
        <div *ngIf="getImageFiles().length === 0" class="text-center py-12">
          <svg class="w-16 h-16 text-gray-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p class="text-gray-400">Geen afbeeldingen gevonden</p>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Image Modal (Lightbox) -->
<div *ngIf="showImageModal && currentImage" 
     class="fixed inset-0 overflow-hidden bg-black/95 backdrop-blur-sm"
     style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; z-index: 9999;"
     (click)="closeImageModal()">
  <!-- Close Button -->
  <button
    (click)="closeImageModal()"
    class="absolute top-4 right-4 z-10 p-2 bg-gray-800/80 rounded-lg hover:bg-gray-700 transition-colors text-white"
    title="Sluiten (ESC)"
  >
    <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
    </svg>
  </button>

  <!-- Image Container -->
  <div class="flex items-center justify-center h-full w-full p-4" (click)="$event.stopPropagation()">
    <!-- Previous Button -->
    <button
      *ngIf="getImageFiles().length > 1"
      (click)="previousImage()"
      class="absolute left-4 z-10 p-3 bg-gray-800/80 rounded-lg hover:bg-gray-700 transition-colors text-white"
      title="Vorige (←)"
    >
      <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
      </svg>
    </button>

    <!-- Image -->
    <div class="flex flex-col items-center justify-center max-w-full max-h-full">
      <img
        [src]="currentImage.url"
        [alt]="currentImage.name"
        [style.transform]="'scale(' + imageZoom + ')'"
        [style.transition]="'transform 0.2s ease-in-out'"
        class="max-w-full max-h-[90vh] object-contain"
        (click)="$event.stopPropagation()"
      />
      
      <!-- Image Info -->
      <div class="mt-4 text-center text-white">
        <p class="text-sm font-medium">{{ currentImage.name }}</p>
        <p class="text-xs text-gray-400 mt-1">
          {{ currentImageIndex + 1 }} / {{ getImageFiles().length }}
        </p>
      </div>
    </div>

    <!-- Next Button -->
    <button
      *ngIf="getImageFiles().length > 1"
      (click)="nextImage()"
      class="absolute right-4 z-10 p-3 bg-gray-800/80 rounded-lg hover:bg-gray-700 transition-colors text-white"
      title="Volgende (→)"
    >
      <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
      </svg>
    </button>
  </div>

</div>

<!-- Delete Confirmation Modal -->
<div *ngIf="showDeleteModal" class="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="delete-modal-title" role="dialog" aria-modal="true">
  <div class="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
    <!-- Background backdrop -->
    <div class="fixed inset-0 bg-gray-900/75 backdrop-blur-sm transition-opacity" (click)="closeDeleteModal()"></div>

    <!-- Modal container -->
    <div class="relative w-full max-w-md transform overflow-hidden rounded-xl bg-gray-800 shadow-2xl transition-all border border-gray-700/50" (click)="$event.stopPropagation()">
      <!-- Header -->
      <div class="flex items-center justify-between border-b border-gray-700/50 px-4 py-3 sm:px-5 sm:py-3.5">
        <h3 class="text-lg font-semibold text-gray-100" id="delete-modal-title">
          Bestand Verwijderen
        </h3>
        <button
          (click)="closeDeleteModal()"
          class="text-gray-400 hover:text-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded-lg p-1"
          aria-label="Sluiten"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Modal content -->
      <div class="px-4 py-4 sm:px-5 sm:py-5">
        <div class="flex items-center mb-4">
          <div class="flex-shrink-0">
            <svg class="w-12 h-12 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-sm text-gray-300">
              Weet je zeker dat je <span class="font-semibold text-gray-200">{{ fileToDelete?.name }}</span> wilt verwijderen?
            </p>
            <p class="text-xs text-gray-500 mt-1">Deze actie kan niet ongedaan worden gemaakt.</p>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="flex items-center justify-end gap-3 border-t border-gray-700/50 px-4 py-3 sm:px-5 sm:py-3.5">
        <button
          (click)="closeDeleteModal()"
          class="inline-flex items-center px-4 py-2 border border-gray-600 text-sm font-medium rounded-md text-gray-300 bg-gray-700 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
        >
          Annuleren
        </button>
        <button
          (click)="fileToDelete && deleteFile(fileToDelete)"
          class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
        >
          Verwijderen
        </button>
      </div>
    </div>
  </div>
</div>

